import React from 'react';
import { Calendar, Utensils, DollarSign, Clock, Target, TrendingUp } from 'lucide-react';
import { NumberInput } from '../../common/NumberInput';

export default function SubscriptionDetailsTab({ values, setField, t }) {
  return (
    <div className="space-y-8">
      {/* Configuration Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg mr-3">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            {t('subscriptionConfiguration')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{t('configureBasicSettings')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center mb-2">
                <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">{t("expiryDays")}</span>
              </div>
              <NumberInput
                name="expiryDays"
                value={values.expiryDays}
                onChange={(e) => setField("expiryDays", e.target.value)}
                min={1}
                required
                help={t("expiryDaysHelp")}
                className="focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center mb-2">
                <Utensils className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">{t("mealsPerDay")}</span>
              </div>
              <NumberInput
                name="mealsPerDay"
                value={values.mealsPerDay}
                onChange={(e) => setField("mealsPerDay", e.target.value)}
                min={1}
                required
                help={t("mealsPerDayHelp")}
                className="focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center mb-2">
                <DollarSign className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">{t("pricePerDay")}</span>
              </div>
              <NumberInput
                name="pricePerDay"
                value={values.pricePerDay}
                onChange={(e) => setField("pricePerDay", e.target.value)}
                min={0}
                step={0.01}
                required
                help={t("pricePerDayHelp")}
                className="focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Preview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="p-2 bg-green-100 rounded-lg mr-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            {t('keyFeatures')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{t('previewSubscriptionValue')}</p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center border border-green-200">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-green-700 mb-2">
                {values.expiryDays || 0}
              </div>
              <div className="text-sm font-medium text-green-600">{t('daysValid')}</div>
              <div className="text-xs text-gray-500 mt-1">{t('subscriptionDuration')}</div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border border-blue-200">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                <Utensils className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-blue-700 mb-2">
                {values.mealsPerDay || 0}
              </div>
              <div className="text-sm font-medium text-blue-600">{t('mealsDaily')}</div>
              <div className="text-xs text-gray-500 mt-1">{t('dailyMealAllowance')}</div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center border border-purple-200">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-4">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-3xl font-bold text-purple-700 mb-2">
                {values.pricePerDay || 0}
              </div>
              <div className="text-sm font-medium text-purple-600">{t('dailyPrice')} (JD)</div>
              <div className="text-xs text-gray-500 mt-1">{t('costPerDay')}</div>
            </div>
          </div>
          
          {/* Total Value Calculation */}
          {values.expiryDays && values.pricePerDay && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border-l-4 border-indigo-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('totalSubscriptionValue')}</p>
                  <p className="text-xs text-gray-500">{t('basedOnConfiguration')}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-indigo-600">
                    {(values.expiryDays * values.pricePerDay).toFixed(2)} JD
                  </p>
                  <p className="text-xs text-gray-500">
                    {values.expiryDays * values.mealsPerDay || 0} {t('totalMeals')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
